/**
 * Moteur du diagnostic « pourquoi mon post LinkedIn n'a pas de vues ».
 *
 * La logique s'appuie sur le mécanisme décrit dans nos propres articles
 * (`premiere-heure-post-linkedin.md`, `post-linkedin-sans-engagement.md`,
 * `meilleur-moment-pour-poster-sur-linkedin.md`) : LinkedIn ne diffuse pas un
 * post à tout le réseau, il le teste d'abord sur un petit échantillon
 * (composé de contacts récents et actuellement connectés) et n'élargit la
 * diffusion que si ce groupe réagit dans l'heure. Rien ici n'invente un
 * chiffre : le créneau mardi-jeudi 11h-17h vient de l'étude Sprout Social
 * citée dans l'article sur le meilleur moment pour poster, et le rôle du
 * format vient du "dwell time" documenté par le blog d'ingénierie LinkedIn,
 * cité dans le même article.
 */

export type PostFormat = 'text' | 'image' | 'carousel' | 'video';
export type PostingWindow = 'weekday_midday' | 'weekday_other' | 'weekend' | 'unknown';
export type NetworkSize = 'small' | 'medium' | 'large' | 'unknown';
export type FirstHourSignal = 'zero' | 'few' | 'several' | 'unknown';

export interface DiagnosticInput {
  format: PostFormat;
  window: PostingWindow;
  network: NetworkSize;
  signal: FirstHourSignal;
}

export interface Diagnosis {
  headline: string;
  paragraphs: string[];
  tip: string;
}

const FORMAT_TIP: Record<PostFormat, string> = {
  text: "Un post texte seul n'a rien qui retienne le regard une fraction de seconde de plus : le signal social (réactions, commentaires) porte tout le poids du test.",
  image:
    "Une image ajoute un peu de temps de lecture (le « dwell time » que LinkedIn mesure), mais ça n'a jamais remplacé un vrai signal social dans l'heure.",
  carousel:
    "Un carrousel ne compte comme lu que si on le swipe : arrêté à la première diapo, LinkedIn le voit à peine mieux qu'un post ignoré.",
  video:
    "Une vidéo peut gonfler le temps passé sur le post sans qu'il y ait un seul like dessous : ce temps de visionnage aide, mais ne remplace pas réactions et commentaires.",
};

/**
 * Chaque question a une échappatoire « je ne sais pas » : elle est repliée
 * sur l'hypothèse la plus fréquente (créneau et réseau faibles par défaut,
 * signal « faible ») pour que l'outil rende toujours un diagnostic exploitable,
 * jamais un blocage.
 */
export function diagnose({ format, window, network, signal }: DiagnosticInput): Diagnosis {
  const formatTip = FORMAT_TIP[format];
  const effectiveSignal = signal === 'unknown' ? 'few' : signal;
  const isThinWindow = window === 'weekend' || window === 'weekday_other' || window === 'unknown';
  const isThinNetwork = network === 'small' || network === 'unknown';

  if (effectiveSignal === 'several') {
    return {
      headline: 'Le test de diffusion a été passé : le problème est ailleurs.',
      paragraphs: [
        "Trois réactions ou plus dans la première heure, c'est un signal suffisant pour que LinkedIn propose votre post à un échantillon plus large. L'impression de « pas de vues » vient probablement d'autre chose : un compteur d'impressions regardé trop tôt, un deuxième palier qui n'a pas suivi, ou un contenu qui tient le premier public mais ne donne pas envie de le repartager.",
        "Regardez le nombre d'impressions dans les statistiques du post (pas seulement les réactions) et le taux de clic sur « voir plus » : c'est souvent là, pas dans l'heure zéro, que ça se joue pour un post qui a déjà passé le premier test.",
      ],
      tip: "Pour le prochain post : gardez un œil sur les commentaires dans l'heure qui suit et répondez vite : ça relance le test sur un public encore plus large.",
    };
  }

  if (isThinWindow) {
    return {
      headline: 'La cause la plus probable : un échantillon de test trop réduit à cet horaire.',
      paragraphs: [
        `Publier ${
          window === 'weekend' ? 'le week-end' : 'en dehors du créneau mardi-jeudi, 11h-17h'
        } réduit mécaniquement le nombre de personnes connectées au moment où LinkedIn teste votre post sur un premier petit groupe. Si ce groupe est clairsemé, personne ne produit le signal qui déclenche une diffusion plus large, indépendamment de la qualité du texte.`,
        formatTip,
        isThinNetwork
          ? "Avec un réseau de moins de 500 relations, ce premier échantillon est déjà mince par nature : l'effet d'un horaire creux s'ajoute à un effet de taille, pas l'un ou l'autre."
          : "Votre réseau est assez grand pour fournir un bon échantillon : c'est surtout l'horaire qui l'a vidé cette fois-ci.",
      ],
      tip: 'Republiez un post comparable un mardi, mercredi ou jeudi entre 11h et 17h (heure de votre audience), et restez disponible pour répondre aux premiers commentaires dans les 60 minutes.',
    };
  }

  return {
    headline: "La cause la plus probable : l'échantillon était là, mais il n'a pas réagi.",
    paragraphs: [
      "Vous avez publié à un moment où votre réseau est normalement actif, ce qui écarte la piste de l'horaire. Le premier groupe testé existait, mais n'a pas produit assez de signal dans l'heure pour que LinkedIn élargisse la diffusion. Ce mécanisme ne dépend pas que du texte : qui était connecté à cet instant précis, et ce qui se disputait la même attention, comptent aussi.",
      formatTip,
      isThinNetwork
        ? "Un réseau de moins de 500 relations donne un échantillon de départ mécaniquement réduit, quel que soit l'horaire : c'est probablement le facteur qui pèse le plus ici."
        : "Avec un réseau de cette taille, un échantillon absent tient souvent à la concurrence pour l'attention à ce moment précis, plus qu'à une audience trop petite.",
    ],
    tip: "Pour le prochain post : restez sur l'app dans les 15 minutes qui suivent la publication pour répondre à tout commentaire. C'est le geste qui pèse le plus sur ce premier test.",
  };
}
